/** e.g. Efsc2026-25aventineexhibit-1-sales_comp.pdf → "Exhibit 1 Sales comp" */
export const NYSCEF_EXHIBIT_FILENAME_RE =
  /(?:(town|aventine)[-_]?)?(exhibit|letter)-?([A-Za-z0-9]+)?(?:[-_\s]+(.+))?$/i;

/** Trailing unique key on newer filenames, e.g. aventine-assignment-ac28e883 */
const NYSCEF_SUFFIX_KEY_RE = /-[a-f0-9]{8}$/i;

/** Exhibit PDF name for a zip entry: original name, without the trailing hash. */
export const uniqueNyscefDownloadFilename = (filename, used) => {
  const base = String(filename || '').trim().split(/[/\\]/).pop().split('?')[0];
  const stem = base.replace(/\.pdf$/i, '').replace(NYSCEF_SUFFIX_KEY_RE, '').replace(/[<>:"/\\|?*]/g, '').trim() || 'document';
  let name = `${stem}.pdf`;
  const key = name.toLowerCase();
  if (!used.has(key)) {
    used.add(key);
    return name;
  }
  let n = 2;
  while (used.has(`${stem}_${n}.pdf`.toLowerCase())) n += 1;
  name = `${stem}_${n}.pdf`;
  used.add(name.toLowerCase());
  return name;
};

export const EVIDENCE_PARTIES = {
  TOWN: 'town',
  AVENTINE: 'aventine',
};

/** Drop leading SCAR index tokens: EFSC2026-71, Efsc2026 71, E012026026024, etc. */
const stripScarIndexPrefix = (name) => {
  const original = String(name || '').trim();
  const tokens = original.replace(/[_/]/g, ' ').trim().split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    const next = tokens[i + 1];
    if (/\d/.test(tok) || (/^[A-Za-z]+$/.test(tok) && next && /\d/.test(next))) {
      i += 1;
      continue;
    }
    break;
  }
  return tokens.slice(i).join(' ').trim() || original;
};

/** Index (EFSC2026-25, Efsc2026 71, 071396-2026, E012026026024), then party, optionally separated. */
const PARTY_AFTER_INDEX_RE =
  /^(?:[a-z]*\d+(?:[-_\s]+\d+)*[-_\s]*)?(aventine|town(?=exhibit|letter|[-_\s]|$))/;

const inferPartyFromName = (name) => {
  const lower = String(name || '').toLowerCase();
  const marker = lower.match(PARTY_AFTER_INDEX_RE);
  if (marker) return marker[1]; // 'aventine' | 'town', same strings as EVIDENCE_PARTIES
  if (/exhibit|townletter/.test(lower)) return EVIDENCE_PARTIES.TOWN;
  return null;
};

export const humanizeUnderscoreSegment = (segment) => {
  const words = String(segment).replace(/_/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  return [
    words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase(),
    ...words.slice(1).map((w) => w.toLowerCase()),
  ].join(' ');
};

export const getNyscefRawFilename = (file) => {
  const raw = file?.Filename ?? file?.filename ?? (file?.Key ? file.Key.split('/').pop() : '') ?? '';
  const noQuery = String(raw).split('?')[0];
  return noQuery.includes('/') ? noQuery.split('/').pop() : noQuery;
};

export const parseNyscefExhibit = (filename) => {
  const base = String(filename).trim().split('/').pop().replace(/\.pdf$/i, '').replace(NYSCEF_SUFFIX_KEY_RE, '');
  if (!base) {
    return { party: null, groupLabel: '', fullLabel: '' };
  }

  const party = inferPartyFromName(base);
  const exhibitMatch = base.match(NYSCEF_EXHIBIT_FILENAME_RE);
  const [, capturedParty, docKind, exhibitId, description] = exhibitMatch || [];
  const kind = String(docKind || '').toLowerCase();
  if (kind && (capturedParty || kind === 'exhibit')) {
    const title = humanizeUnderscoreSegment(String(description || '').replace(/-/g, ' '));
    // Snap manual uploads are filed as exhibit z, then Z2, Z3, ...
    const manual = kind === 'exhibit' && String(exhibitId || '').match(/^z(\d*)$/i);
    if (manual) {
      const groupLabel = `Manual${title ? ` ${title}` : ''}${manual[1] ? ` #${manual[1]}` : ''}`;
      return { party, groupLabel, fullLabel: groupLabel };
    }
    const kindLabel = kind === 'letter' ? 'Letter' : 'Exhibit';
    const idLabel = exhibitId && /^[a-z]$/i.test(exhibitId) ? exhibitId.toUpperCase() : exhibitId;
    const groupLabel = `${kindLabel}${idLabel ? ` ${idLabel}` : ''}${title ? ` ${title}` : ''}`;
    return { party, groupLabel, fullLabel: groupLabel };
  }

  const cut = base.search(/(?:town|aventine)(?:exhibit|letter|[-_])|(?:exhibit|letter)/i);
  const rest = cut >= 0 ? base.slice(cut) : base;
  const withoutParty = rest.replace(/^(?:town|aventine)[-_]?/i, '').replace(/-/g, ' ');
  const withoutScar = party ? withoutParty : stripScarIndexPrefix(withoutParty);
  const fallback = humanizeUnderscoreSegment(withoutScar || withoutParty || rest);
  return { party, groupLabel: fallback, fullLabel: fallback };
};

export const displayFileLabel = (file, index) => {
  const s = String(getNyscefRawFilename(file)).trim();
  if (!s) return `File ${index + 1}`;
  const { fullLabel } = parseNyscefExhibit(s);
  return fullLabel || s;
};

export const nyscefLastModifiedTime = (file) => {
  const lm = file?.LastModified;
  if (lm == null || lm === '') return null;
  const t = new Date(lm).getTime();
  return Number.isNaN(t) ? null : t;
};

/** "Uploaded at mm/dd/yy, h:42am EST" in America/New_York */
export const formatNyscefLastModifiedEst = (file) => {
  const ms = nyscefLastModifiedTime(file);
  if (ms == null) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(new Date(ms));
  const pick = (type) => parts.find((p) => p.type === type)?.value ?? '';
  const month = pick('month');
  const day = pick('day');
  const year = pick('year');
  const hour = pick('hour');
  const minute = pick('minute');
  const ap = (pick('dayPeriod') || '').toLowerCase();
  if (!month || !day || !year) return null;
  return `Uploaded at ${month}/${day}/${year}, ${hour}:${minute}${ap} EST`;
};

const DUPLICATE_EXHIBIT_RE = /_\(duplicate[^)]*\)/i;

const exhibitSortMeta = (entry) => {
  const raw = getNyscefRawFilename(entry.file);
  return {
    isDuplicate: DUPLICATE_EXHIBIT_RE.test(raw),
    base: String(raw).replace(/\.pdf$/i, '').replace(DUPLICATE_EXHIBIT_RE, '').toLowerCase(),
  };
};

const compareExhibitEntries = (a, b) => {
  const ta = nyscefLastModifiedTime(a.file);
  const tb = nyscefLastModifiedTime(b.file);
  if (ta != null && tb != null && ta !== tb) return ta - tb;
  if (ta == null && tb != null) return 1;
  if (tb == null && ta != null) return -1;
  return a.listIndex - b.listIndex;
};

const sortExhibitEntries = (entries) => {
  const groups = new Map();
  for (const entry of entries) {
    const { base } = exhibitSortMeta(entry);
    const group = groups.get(base);
    if (group) group.push(entry);
    else groups.set(base, [entry]);
  }
  return [...groups.values()]
    .map((group) =>
      group.sort((a, b) => {
        const da = exhibitSortMeta(a).isDuplicate;
        const db = exhibitSortMeta(b).isDuplicate;
        if (da !== db) return da ? 1 : -1;
        return compareExhibitEntries(a, b);
      })
    )
    .sort((ga, gb) => compareExhibitEntries(ga[0], gb[0]))
    .flat();
};

/** Same buckets as the NYSCEF tab: Aventine, Town, and everything else as Other. */
export const groupNyscefExhibits = (files) => {
  const list = (Array.isArray(files) ? files : []).filter((f) => f?.Url);
  const buckets = { aventine: [], town: [], other: [] };
  list.forEach((file, listIndex) => {
    const raw = String(getNyscefRawFilename(file)).trim();
    const { party, groupLabel, fullLabel } = parseNyscefExhibit(raw);
    const label = fullLabel || displayFileLabel(file, listIndex);
    const section = party === EVIDENCE_PARTIES.AVENTINE ? 'aventine' : party === EVIDENCE_PARTIES.TOWN ? 'town' : 'other';
    buckets[section].push({
      file,
      listIndex,
      groupLabel: groupLabel || label,
      fullLabel: label,
    });
  });
  return {
    aventine: sortExhibitEntries(buckets.aventine),
    town: sortExhibitEntries(buckets.town),
    other: sortExhibitEntries(buckets.other),
  };
};

export const filterNyscefFilesByParty = (files, party) => {
  const target = String(party || '').toLowerCase();
  if (!target) return [];
  return (Array.isArray(files) ? files : []).filter((file) => {
    if (!file?.Url) return false;
    const raw = String(getNyscefRawFilename(file)).trim();
    const { party: fileParty } = parseNyscefExhibit(raw);
    return fileParty === target;
  });
};

export const countCasesWithPartyEvidence = (cases, party) => {
  if (!Array.isArray(cases) || !cases.length) return 0;
  return cases.filter((c) => filterNyscefFilesByParty(c?.NyscefS3Files, party).length > 0).length;
};
